/**
 * Number Formatting Utilities
 *
 * Provides consistent number formatting across the application using
 * European/Latvian locale standards:
 * - Thousand separator: space (e.g., 1 234 567)
 * - Decimal separator: comma (e.g., 1 234,56)
 *
 * @example
 * formatCurrency(1234.56) // "1 234,56"
 * formatNumber(1234567) // "1 234 567"
 * formatPercent(12.5) // "12,50"
 * parseCurrencyInput("1 234,56") // 1234.56
 */

// Locale identifier (kept for reference/future use)
// const LOCALE = "lv-LV";

/**
 * Helper to format number with space as thousand separator
 * Ensures consistent formatting regardless of locale quirks
 */
const formatWithThousandSeparator = (
  value: number,
  decimals: number
): string => {
  // Handle negative numbers
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Split into integer and decimal parts
  const fixed = absValue.toFixed(decimals);
  const parts = fixed.split(".");
  const intPart = parts[0] || "0";
  const decPart = parts[1];

  // Add thousand separators (space) to integer part - manually
  let formattedInt = "";
  const len = intPart.length;
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 3 === 0) {
      formattedInt += " ";
    }
    formattedInt += intPart[i];
  }

  // Return with comma as decimal separator
  const sign = isNegative ? "-" : "";
  if (decimals > 0 && decPart) {
    return `${sign}${formattedInt},${decPart}`;
  }
  return `${sign}${formattedInt}`;
};

/**
 * Format a number as currency (with 2 decimal places by default)
 * Used for displaying money amounts, premiums, discounts, etc.
 *
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1 234,56")
 */
export const formatCurrency = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  return formatWithThousandSeparator(value, decimals);
};

/**
 * Format a number with currency symbol prefix
 *
 * @param value - The numeric value to format
 * @param symbol - Currency symbol (default: "€")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with symbol (e.g., "€1 234,56")
 */
export const formatCurrencyWithSymbol = (
  value: number | null | undefined,
  symbol: string = "€",
  decimals: number = 2
): string => {
  const formatted = formatCurrency(value, decimals);
  if (!formatted) return "";
  return `${symbol}${formatted}`;
};

/**
 * Format a number without decimal places (for whole numbers)
 * Used for displaying counts, IDs, years, etc.
 *
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string (e.g., "1 234 567")
 */
export const formatNumber = (
  value: number | null | undefined,
  decimals: number = 0
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  return formatWithThousandSeparator(value, decimals);
};

/**
 * Format a percentage value
 *
 * @param value - The numeric value to format (e.g., 12.5 for 12.5%)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "12,50")
 */
export const formatPercent = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  return formatCurrency(value, decimals);
};

/**
 * Parse a formatted currency/number string back to a number
 * Handles European format with spaces and commas
 *
 * @param value - The formatted string to parse (e.g., "1 234,56")
 * @returns Parsed number or 0 if invalid
 */
export const parseCurrencyInput = (
  value: string | null | undefined
): number => {
  if (!value || typeof value !== "string") {
    return 0;
  }

  // Remove currency symbols and trim
  let cleaned = value.replace(/[€$£¥]/g, "").trim();

  // Remove thousand separators (spaces and dots used as thousand sep)
  cleaned = cleaned.replace(/\s/g, "");

  // Handle European format: replace comma with dot for decimal
  // But first check if there's a dot used as thousand separator
  // European: 1.234,56 -> 1234.56
  // If there's both dot and comma, dot is thousand separator
  if (cleaned.includes(",") && cleaned.includes(".")) {
    // European format: dots are thousand separators, comma is decimal
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(",")) {
    // Only comma: it's the decimal separator
    cleaned = cleaned.replace(",", ".");
  }
  // If only dot, it's already in US format

  const result = parseFloat(cleaned);
  return isNaN(result) ? 0 : result;
};

/**
 * Format a number for display in an input field
 * Returns empty string for zero/null to allow clearing inputs
 *
 * @param value - The numeric value
 * @param decimals - Number of decimal places
 * @param showZero - Whether to show "0" or empty string for zero value
 * @returns Formatted string for input display
 */
export const formatForInput = (
  value: number | null | undefined,
  decimals: number = 2,
  showZero: boolean = false
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  if (value === 0 && !showZero) {
    return "";
  }

  return formatCurrency(value, decimals);
};

/**
 * Format a number for form state (as string with fixed decimals)
 * Used when setting form values that need to be strings
 *
 * @param value - The numeric value
 * @param decimals - Number of decimal places (default: 2)
 * @returns String representation with fixed decimals (e.g., "1234.56")
 */
export const formatForFormState = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "";
  }

  return value.toFixed(decimals);
};

/**
 * Validate if a string can be parsed as a valid number
 *
 * @param value - The string to validate
 * @returns True if valid number format
 */
export const isValidNumberInput = (value: string): boolean => {
  if (!value || value.trim() === "") {
    return true; // Empty is valid (will be handled as 0 or required validation)
  }

  // Allow digits, spaces (thousand sep), comma/dot (decimal sep), and minus
  const pattern = /^-?[\d\s.,]*$/;
  if (!pattern.test(value)) {
    return false;
  }

  // Try to parse it
  const parsed = parseCurrencyInput(value);
  return !isNaN(parsed);
};

/**
 * Format time duration in milliseconds to human-readable format
 *
 * @param ms - Milliseconds
 * @returns Formatted string (e.g., "1,23s" or "500ms")
 */
export const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(2).replace(".", ",")}s`;
};

/**
 * Safe number conversion with fallback
 *
 * @param value - Value to convert
 * @param fallback - Fallback value if conversion fails (default: 0)
 * @returns Numeric value or fallback
 */
export const toNumber = (
  value: string | number | null | undefined,
  fallback: number = 0
): number => {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (typeof value === "number") {
    return isNaN(value) ? fallback : value;
  }

  const parsed = parseCurrencyInput(value);
  return isNaN(parsed) ? fallback : parsed;
};
